import { useQuery } from '@tanstack/react-query';
import { EXPIRY_WARNING_DAYS, daysBetween, todayIso, toIsoDate } from '@budget/core';
import { supabase } from '../../lib/supabase.js';
import { queryKeys } from '../../lib/query-client.js';
import type { IntegrationRow, SyncRunRow } from '../../types/db.js';

export interface ExpiringIntegration {
  id: string;
  provider: string;
  expiresAt: string;
  daysLeft: number;
}

/** The last run of each sync job — what the dashboard's status panel shows. */
export function useLatestSyncRuns() {
  return useQuery({
    queryKey: queryKeys.syncRuns,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<SyncRunRow[]> => {
      const { data, error } = await supabase
        .from('sync_runs')
        .select('*')
        .order('started_at', { ascending: false })
        .limit(50);

      if (error) throw error;

      // Collapse to the newest run per job; the rest is history for the detail view.
      const newestPerJob = new Map<string, SyncRunRow>();
      for (const run of data ?? []) {
        if (!newestPerJob.has(run.job)) newestPerJob.set(run.job, run);
      }
      return [...newestPerJob.values()];
    },
  });
}

/**
 * Integrations whose credential dies within the warning window.
 *
 * An IBKR token or a bank consent that quietly expires stops the sync without
 * any visible failure, so the warning has to appear well before it happens.
 */
export function useExpiringIntegrations(withinDays: number = EXPIRY_WARNING_DAYS) {
  return useQuery({
    queryKey: [...queryKeys.integrations, 'expiring', withinDays],
    staleTime: 60 * 60 * 1000,
    queryFn: async (): Promise<ExpiringIntegration[]> => {
      const { data, error } = await supabase
        .from('integrations')
        .select('*')
        .not('expires_at', 'is', null)
        .neq('status', 'disabled')
        .order('expires_at', { ascending: true });

      if (error) throw error;

      const today = todayIso();
      return (data ?? [])
        .flatMap((row: IntegrationRow) => {
          if (!row.expires_at) return [];
          const daysLeft = daysBetween(today, toIsoDate(new Date(row.expires_at)));
          if (daysLeft > withinDays) return [];
          return [
            {
              id: row.id,
              provider: row.provider,
              expiresAt: row.expires_at,
              daysLeft,
            },
          ];
        })
        .sort((a, b) => a.daysLeft - b.daysLeft);
    },
  });
}
